import React, { useState } from "react"
import { motion } from "framer-motion"
import { useTranslation } from "react-i18next"
import {
  Building2,
  HeartPulse,
  ShieldCheck,
  Radiation,
  Dna as DnaIcon,
  ShoppingBag,
  Briefcase,
  Gavel,
  GraduationCap,
  FileCheck,
  Lock,
} from "lucide-react"
import { useNavigationStore } from "@/stores/navigation"
import CustomerLogos from "@/components/common/CustomerLogos"
import DownloadForm from "@/components/common/DownloadForm"

const Section = ({
  id,
  children,
  className = "",
}: {
  id: string
  children: React.ReactNode
  className?: string
}) => (
  <section
    id={id}
    className={`mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}
  >
    {children}
  </section>
)

const Customers: React.FC = () => {
  const { t } = useTranslation()
  const { openDemoModal } = useNavigationStore()
  
  // Download form state
  const [downloadFormOpen, setDownloadFormOpen] = useState(false)

  const handleDownloadProductOverview = () => {
    setDownloadFormOpen(true)
  }


  const ORG_TYPES = [
    {
      key: "hospitals",
      title: t("customers.organizations.hospitals.title"),
      blurb: t("customers.organizations.hospitals.description"),
      Icon: Building2,
      color: "bg-blue-50 text-blue-700",
    },
    {
      key: "public-private",
      title: t("customers.organizations.publicPrivateHealth.title"),
      blurb: t("customers.organizations.publicPrivateHealth.description"),
      Icon: HeartPulse,
      color: "bg-green-50 text-green-700",
    },
    {
      key: "insurers",
      title: t("customers.organizations.insurers.title"),
      blurb: t("customers.organizations.insurers.description"),
      Icon: ShieldCheck,
      color: "bg-purple-50 text-purple-700",
    },
    {
      key: "cancer-centers",
      title: t("customers.organizations.cancerCenters.title"),
      blurb: t("customers.organizations.cancerCenters.description"),
      Icon: Radiation,
      color: "bg-pink-50 text-pink-700",
    },
    {
      key: "cell-therapy",
      title: t("customers.organizations.cellTherapyCenters.title"),
      blurb: t("customers.organizations.cellTherapyCenters.description"),
      Icon: DnaIcon,
      color: "bg-indigo-50 text-indigo-700",
    },
    {
      key: "retail-clinics",
      title: t("customers.organizations.retailClinics.title"),
      blurb: t("customers.organizations.retailClinics.description"),
      Icon: ShoppingBag,
      color: "bg-yellow-50 text-yellow-700",
    },
    {
      key: "employers",
      title: t("customers.organizations.employers.title"),
      blurb: t("customers.organizations.employers.description"),
      Icon: Briefcase,
      color: "bg-orange-50 text-orange-700",
    },
    {
      key: "corrections",
      title: t("customers.organizations.corrections.title"),
      blurb: t("customers.organizations.corrections.description"),
      Icon: Gavel,
      color: "bg-red-50 text-red-700",
    },
    {
      key: "education",
      title: t("customers.organizations.education.title"),
      blurb: t("customers.organizations.education.description"),
      Icon: GraduationCap,
      color: "bg-teal-50 text-teal-700",
    },
  ]

  return (
    <div className="min-h-screen w-full bg-white text-slate-800">
      {/* HERO — match ReferralCX style */}
      <header
        className="w-full text-white"
        style={{
          background: "linear-gradient(90deg, #395091 0%, #559EBA 100%)",
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {/* Product Wordmark */}
          <div className="mb-6 sm:mb-8">
            <h2
              className="text-6xl sm:text-7xl md:text-8xl leading-none tracking-tight"
              style={{
                fontFamily:
                  "Montserrat, Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
              }}
            >
              <span>Eceptionist</span>
              <span className="font-bold text-[#7AC943]">CX</span>
              <sup className="align-super text-2xl sm:text-3xl md:text-4xl ml-1">
                ™
              </sup>
            </h2>
          </div>

          {/* Headline & Subcopy */}
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
              {t("customers.hero.title")}
            </h1>
            <p className="text-base sm:text-lg md:text-xl/8 opacity-95">
              {t("customers.hero.subtitle")}
            </p>
            <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <button
                onClick={openDemoModal}
                className="w-full sm:w-auto text-center bg-white text-[#395091] font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-gray-100 transition"
              >
                {t("common.bookDemo")}
              </button>
              <a
                href="#orgs"
                className="w-full sm:w-auto text-center bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/20 transition"
              >
                {t("customers.hero.exploreWhoWeServe")}
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* INTRO SUMMARY */}
      <Section id="summary" className="py-8 sm:py-10">
        <div className="rounded-3xl bg-white/60 p-6 shadow-[0_1px_0_rgba(31,43,89,0.06),0_8px_24px_rgba(31,43,89,0.06)]">
          <p
            className="text-[15px] text-[#1F2B59]/90 sm:text-base"
            dangerouslySetInnerHTML={{
              __html: t("customers.summary.description"),
            }}
          />
          <p
            className="mt-4 text-[15px] text-[#1F2B59]/90 sm:text-base"
            dangerouslySetInnerHTML={{
              __html: t("customers.summary.telehealthSupport"),
            }}
          />
        </div>
      </Section>

      {/* ORG GRID */}
      <Section
        id="orgs"
        className="py-8 sm:py-10 bg-gradient-to-br from-[#F9FAFB] to-[#F0F4FA] scroll-mt-24 lg:scroll-mt-28"
      >
        <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
          {t("customers.organizations.title")}
        </h2>
        <p className="mt-2 max-w-3xl text-sm text-[#31407A] sm:text-base">
          {t("customers.organizations.subtitle")}
        </p>

        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-3">
          {ORG_TYPES.map((item, idx) => (
            <motion.div
              key={item.key}
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.35, delay: idx * 0.03 }}
              className="group relative overflow-hidden rounded-3xl border border-[#1F2B59]/10 bg-white/80 p-5 shadow-sm transition hover:shadow-md"
            >
              <div className="flex items-start gap-4 min-h-28">
                <div
                  className={`shrink-0 flex size-14 items-center justify-center rounded-2xl ${item.color.split(" ")[0]}`}
                >
                  <item.Icon
                    className={`h-7 w-7 ${item.color.split(" ")[1]}`}
                  />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">
                    {item.title}
                  </h3>
                  <p className="mt-1 text-sm text-[#31407A]">{item.blurb}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* SOCIAL PROOF */}
      <Section id="logos" className="py-8 sm:py-12">
        <div className="rounded-3xl border border-[#1F2B59]/10 bg-white p-6">
          <p className="text-center text-base sm:text-lg font-medium text-[#31407A]">
            {t("customers.socialProof.title")}
          </p>
          <CustomerLogos 
            count={9}
            mode="card"
            className="mt-6 grid grid-cols-1 sm:grid-cols-3 justify-items-center gap-4 sm:gap-6"
          />
        </div>
      </Section>

      {/* TRUSTED & COMPLIANT */}
      <Section
        id="trusted"
        className="border-t border-slate-200 py-12 sm:py-14"
      >
        <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
          {t("customers.trustedCompliant.title")}
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          <div className="flex flex-col items-center gap-2">
            <ShieldCheck className="w-8 h-8 text-[#395091]" />
            <span className="text-slate-700 font-medium">
              {t("customers.trustedCompliant.hipaa")}
            </span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <FileCheck className="w-8 h-8 text-[#395091]" />
            <span className="text-slate-700 font-medium">
              {t("customers.trustedCompliant.soc2")}
            </span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <Lock className="w-8 h-8 text-[#395091]" />
            <span className="text-slate-700 font-medium">
              {t("customers.trustedCompliant.sso")}
            </span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <ShieldCheck className="w-8 h-8 text-[#395091]" />
            <span className="text-slate-700 font-medium">
              {t("customers.trustedCompliant.gdpr")}
            </span>
          </div>
        </div>
      </Section>

      {/* FINAL CTA (match ReferralCX "ready to improve" style) */}
      <Section id="contact" className="relative mt-6 sm:mt-8">
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(90deg, #395091 0%, #559EBA 100%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 text-white">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 items-center">
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-3">
                {t("customers.cta.title")}
              </h3>
              <p className="opacity-90 text-base sm:text-lg">
                {t("customers.cta.subtitle")}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 lg:justify-end w-full sm:w-auto">
              <button
                onClick={openDemoModal}
                className="w-full sm:w-auto text-center bg-white text-[#395091] font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-gray-100 transition"
              >
                {t("customers.cta.requestDemo")}
              </button>
                <button
                  onClick={handleDownloadProductOverview}
                  className="w-full sm:w-auto text-center bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/20 transition"
                >
                  {t("customers.cta.downloadOverview")}
                </button>
            </div>
          </div>
        </div>
      </Section>

      {/* Download Form Modal */}
      <DownloadForm
        isOpen={downloadFormOpen}
        onClose={() => setDownloadFormOpen(false)}
        paperTitle="EceptionistCX Product Overview"
        pdfPath="/PDF/EceptionistCX_Product_Overview.pdf"
      />
    </div>
  )
}

export default Customers
